import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Stack, TextField, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { listWorkboardDirectories } from 'dashboard409a/services/dashboard';
import { DEFAULT_DROPBOX_ROOT } from '../dropbox.constants';
import type { DropboxFolderOption } from '../dropbox.types';
import {
  buildDropboxPath,
  getDropboxFolderDisplayName,
  stripDropboxRootPrefix,
} from '../dropbox.utils';
import DropboxFolderTreePicker from '../DropboxFolderTreePicker';
import { FOLDER_PICKER_TREE_MAX_HEIGHT_PX } from '../folderPickerPopover.styles';
import { WorkboardFolderPickerProps } from './WorkboardFolderPicker.props';

// Searching the valuation root scans backup storage, so require a real query first.
const MIN_ROOT_QUERY_LENGTH = 2;
const DIRECTORY_STALE_TIME_IN_MS = 1000 * 60 * 5;

type BrowseState = {
  displayQuery: string;
  treeRelativePrefix: string;
  listQuery: string;
};

const normalizeRelativePrefix = (path: string) => {
  const trimmed = path.trim().replace(/^\/+/, '');
  if (!trimmed) {
    return '';
  }
  return trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
};

const normalizeSavedPath = (path: string) => path.trim().replace(/^\/+/, '').replace(/\/+$/, '');

/** Path under the current browse root, e.g. `2020/2020.12.31/12-31-20` under `Acme Corp/`. */
const relativePathFromSavedReference = (savedFolderReference: string, browseRootPrefix: string): string => {
  const normalized = normalizeSavedPath(savedFolderReference);
  if (!normalized) {
    return '';
  }

  const browsePrefix = normalizeSavedPath(browseRootPrefix);
  if (!browsePrefix) {
    return normalized;
  }

  if (normalized === browsePrefix) {
    return '';
  }

  if (normalized.startsWith(`${browsePrefix}/`)) {
    return normalized.slice(browsePrefix.length + 1);
  }

  return normalized.replace(browseRootPrefix, '').replace(/^\/+/, '').replace(/\/+$/, '');
};

/**
 * Backup directory listing uses `prefix` for nesting and `q` only to filter immediate
 * children at that prefix — not nested paths like `2020/2020.12.31/leaf`.
 */
const parseFilterToBrowseState = (filter: string): BrowseState => {
  const displayQuery = filter.trim();
  if (!displayQuery) {
    return { displayQuery: '', treeRelativePrefix: '', listQuery: '' };
  }

  const segments = displayQuery.split('/').filter(Boolean);
  if (segments.length <= 1) {
    return {
      displayQuery,
      treeRelativePrefix: '',
      listQuery: segments[0] ?? '',
    };
  }

  return {
    displayQuery,
    treeRelativePrefix: normalizeRelativePrefix(segments.slice(0, -1).join('/')),
    listQuery: '',
  };
};

const initialDisplayQuery = ({
  savedFolderReference,
  selectedPath,
  companyName,
  browseRootPrefix,
}: {
  savedFolderReference?: string | null;
  selectedPath?: string | null;
  companyName?: string | null;
  browseRootPrefix: string;
}): string => {
  if (savedFolderReference?.trim()) {
    return relativePathFromSavedReference(savedFolderReference, browseRootPrefix) || normalizeSavedPath(savedFolderReference);
  }

  if (selectedPath?.trim()) {
    const relative = relativePathFromSavedReference(selectedPath, browseRootPrefix);
    if (relative) {
      return relative;
    }
    return normalizeSavedPath(selectedPath);
  }

  return companyName?.trim() || '';
};

const WorkboardFolderPicker: FC<WorkboardFolderPickerProps> = ({
  initialPrefix = '',
  dropboxRoot = DEFAULT_DROPBOX_ROOT,
  companyName,
  savedFolderReference,
  selectedPath,
  onSelect,
}) => {
  const queryClient = useQueryClient();
  const browseRootPrefix = useMemo(() => normalizeRelativePrefix(initialPrefix), [initialPrefix]);
  const atRoot = browseRootPrefix.length === 0;

  const [folderFilter, setFolderFilter] = useState(() =>
    initialDisplayQuery({
      savedFolderReference,
      selectedPath,
      companyName,
      browseRootPrefix,
    })
  );
  const [debouncedBrowseState, setDebouncedBrowseState] = useState(() =>
    parseFilterToBrowseState(
      initialDisplayQuery({
        savedFolderReference,
        selectedPath,
        companyName,
        browseRootPrefix,
      })
    )
  );

  const treeRelativePrefix = useMemo(
    () => normalizeRelativePrefix(`${browseRootPrefix}${debouncedBrowseState.treeRelativePrefix}`),
    [browseRootPrefix, debouncedBrowseState.treeRelativePrefix]
  );

  const rootPath = useMemo(
    () =>
      treeRelativePrefix
        ? buildDropboxPath(dropboxRoot, treeRelativePrefix)
        : buildDropboxPath(dropboxRoot, browseRootPrefix || undefined),
    [browseRootPrefix, dropboxRoot, treeRelativePrefix]
  );

  const rootLabel = useMemo(() => {
    if (treeRelativePrefix) {
      return getDropboxFolderDisplayName(treeRelativePrefix);
    }
    if (browseRootPrefix) {
      return getDropboxFolderDisplayName(browseRootPrefix);
    }
    return getDropboxFolderDisplayName(dropboxRoot);
  }, [browseRootPrefix, dropboxRoot, treeRelativePrefix]);

  const selectedAbsolutePath = useMemo(() => {
    if (!selectedPath?.trim()) {
      return null;
    }
    const relative = stripDropboxRootPrefix(selectedPath, [dropboxRoot]) || selectedPath;
    return buildDropboxPath(dropboxRoot, relative);
  }, [dropboxRoot, selectedPath]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedBrowseState(parseFilterToBrowseState(folderFilter));
    }, 350);
    return () => clearTimeout(timer);
  }, [folderFilter]);

  const isRootQueryTooShort =
    atRoot &&
    !debouncedBrowseState.treeRelativePrefix &&
    debouncedBrowseState.listQuery.length < MIN_ROOT_QUERY_LENGTH;
  const canBrowseTree = !isRootQueryTooShort;

  const loadChildren = useCallback(
    async (absolutePath: string): Promise<DropboxFolderOption[]> => {
      const relative = stripDropboxRootPrefix(absolutePath, [dropboxRoot]);
      const prefix = normalizeRelativePrefix(relative);
      const isTreeRoot = buildDropboxPath(absolutePath) === buildDropboxPath(rootPath);
      // Apply the search query only at the browse root. Nested expands list all children.
      const query = isTreeRoot ? debouncedBrowseState.listQuery : '';
      const directories = await queryClient.fetchQuery({
        queryKey: ['workboard-folder-picker', dropboxRoot, prefix, query],
        queryFn: () => listWorkboardDirectories({ prefix, q: query, root: dropboxRoot }),
        staleTime: DIRECTORY_STALE_TIME_IN_MS,
      });
      return directories.map(directory => ({
        name: directory.name,
        pathDisplay: buildDropboxPath(dropboxRoot, directory.path),
      }));
    },
    [debouncedBrowseState.listQuery, dropboxRoot, queryClient, rootPath]
  );

  const handleSelect = useCallback(
    (option: DropboxFolderOption) => {
      const relative = stripDropboxRootPrefix(option.pathDisplay, [dropboxRoot]);
      if (!relative) {
        onSelect(null);
        return;
      }
      onSelect({
        name: option.name,
        path: normalizeRelativePrefix(relative),
      });
    },
    [dropboxRoot, onSelect]
  );

  const searchLabel = atRoot ? 'Search company folder' : 'Folder path';
  const searchPlaceholder = atRoot ? 'Type a company name…' : 'Type a folder path…';

  let folderFilterHelperText: string | undefined;
  if (isRootQueryTooShort) {
    folderFilterHelperText = `Type at least ${MIN_ROOT_QUERY_LENGTH} characters to search company folders`;
  } else if (debouncedBrowseState.treeRelativePrefix) {
    folderFilterHelperText = 'Browsing nested folders — use / to go deeper (e.g. 2020/2020.12.31/folder).';
  }

  return (
    <Stack spacing={1} sx={{ width: '100%' }}>
      <TextField
        size="small"
        fullWidth
        label={searchLabel}
        placeholder={searchPlaceholder}
        value={folderFilter}
        onChange={event => {
          setFolderFilter(event.target.value);
        }}
        helperText={folderFilterHelperText}
      />

      {canBrowseTree ? (
        <DropboxFolderTreePicker
          key={`${rootPath}::${debouncedBrowseState.listQuery}`}
          rootPath={rootPath}
          rootLabel={rootLabel}
          selectedPath={selectedAbsolutePath}
          onSelect={handleSelect}
          onLoadChildren={loadChildren}
          suggestedFolderName={companyName || undefined}
          maxHeight={FOLDER_PICKER_TREE_MAX_HEIGHT_PX}
        />
      ) : (
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.5,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {`Type at least ${MIN_ROOT_QUERY_LENGTH} characters to search company folders in backup storage.`}
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

export default WorkboardFolderPicker;
